import { useEffect, useMemo, useRef, useState } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import BrregApi from 'services/KjederegisteretAdminApi/BrregApi';
import { debounce } from 'lodash';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import { BrregEnhetResponse } from 'services/KjederegisteretAdminApi/BrregApi/types';

interface Props {
  onBrregEnhetSelected: (item: BrregEnhetResponse) => void;
  isValid?: boolean;
  isInvalid?: boolean;
  required?: boolean;
  clearTypeaheadInitiator?: string | number;
  /**
   * Orgnummer vi ønsker å slå opp i brreg
   */
  selectedOrgNr?: string;

  /**
   * Velger skal vise juridisk navn vi har lagret i Kreg databasen. Hvis denne ikke sendes inn brukes verdien vi får fra brreg.
   * Dette for å indikere at vi har en forskjell i KReg og Brreg og QA gjengen må ta stilling til om det da er flere ting som bør oppdateres på enhet, f.eks. enhetsnavn.
   */
  juridiskNavnIKreg?: string;
  setIsLoadingBrreg?: (isLoading: boolean) => void;
}

const BrregTypeAhead = (props: Props) => {
  const [searchResult, setSearchResult] = useState<BrregEnhetResponse[]>();
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useIsMountedRef();
  const typeaheadRef = useRef(null);
  const lastQueryIdRef = useRef(0);
  const lastOrgQueryIdRef = useRef(0);

  const [selected, setSelected] = useState<BrregEnhetResponse | undefined>(undefined);

  const isNumeric = (value: string) => {
    return /^\d+$/.test(value);
  };

  const cleanInput = (input: string) => {
    return input.replaceAll(' ', '').replaceAll('.', '');
  };

  const lookupQueryInBrreg = useMemo(
    () =>
      debounce(async (q: string) => {
        const queryId = ++lastQueryIdRef.current;
        try {
          setIsLoading(true);
          if (q) {
            const cleanedQuery = cleanInput(q);
            if (!isNumeric(cleanedQuery)) {
              const result = await BrregApi.getByName(cleanedQuery);
              if (lastQueryIdRef.current !== queryId) return; // stale
              if (Array.isArray(result) && result.length > 0) {
                setSearchResult(result);
                return;
              }
            } else {
              const result = await BrregApi.getByOrgNr(cleanedQuery);
              if (lastQueryIdRef.current !== queryId) return; // stale
              if (Array.isArray(result) && result.length > 0) {
                setSearchResult(result);
                return;
              }
            }
          }
          // No q or empty results
          setSearchResult([]);
        } catch (err) {
          // Fail quietly but reset state
          // eslint-disable-next-line no-console
          console.error('BrregTypeAhead search error', err);
          setSearchResult([]);
        } finally {
          if (lastQueryIdRef.current === queryId) {
            setIsLoading(false);
          }
        }
      }, 300),
    [],
  );

  // Cleanup debounced handler on unmount to avoid leaks
  useEffect(() => {
    return () => lookupQueryInBrreg.cancel();
  }, [lookupQueryInBrreg]);

  const renderMenuItem = (option: any, props: any, index: number) => {
    const itemDesc = `${option.name} ${option.orgNr}`;
    return (
      <div key={index}>
        <Highlighter search={props.text}>{itemDesc}</Highlighter>
      </div>
    );
  };

  const clearTypeahead = () => {
    if (isMountedRef.current && typeaheadRef.current) {
      // Prefer public API
      typeaheadRef.current.clear();
      // Keep existing behavior to clear search text if supported in current lib version
      if (typeof typeaheadRef.current.setState === 'function') {
        typeaheadRef.current.setState({ text: '' });
      }
    }
  };

  useEffect(() => {
    clearTypeahead();
  }, [props.clearTypeaheadInitiator]);

  const lookupOrgnr = async (orgnr: string) => {
    const queryId = ++lastOrgQueryIdRef.current;
    try {
      setIsLoading(true);
      const result = await BrregApi.getByOrgNr(orgnr);
      if (lastOrgQueryIdRef.current !== queryId) return; // stale
      if (Array.isArray(result) && result.length > 0) {
        const filtered = result.find((x: BrregEnhetResponse) => x.orgNr === props.selectedOrgNr);
        if (filtered) {
          filtered.juridisknavn = props.juridiskNavnIKreg ?? filtered.name;
          setSelected(filtered);
        }
        setSearchResult(result);
      } else {
        setSelected(undefined);
        setSearchResult([]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('BrregTypeAhead orgnr lookup error', err);
      setSelected(undefined);
      setSearchResult([]);
    } finally {
      if (lastOrgQueryIdRef.current === queryId) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isMountedRef.current && props.selectedOrgNr) {
      lookupOrgnr(props.selectedOrgNr);
    } else {
      setSelected(undefined);
    }
  }, [props.selectedOrgNr]);

  useEffect(() => {
    if (props.setIsLoadingBrreg) {
      props.setIsLoadingBrreg(isLoading);
    }
  }, [isLoading]);

  const handleEnhetSelected = (e: BrregEnhetResponse) => {
    setSelected(e);
    props.onBrregEnhetSelected(selected);
  };

  return (
    <AsyncTypeahead
      inputProps={{ required: props.required || undefined }}
      id="brregTypeAhead"
      filterBy={() => true}
      labelKey={(option: any) => `${option.juridisknavn ?? option.name} ${option.orgNr}`}
      renderMenuItemChildren={(option: any, props: any, index: number) =>
        renderMenuItem(option, props, index)
      }
      useCache={false}
      minLength={3}
      clearButton
      isLoading={isLoading}
      options={searchResult ?? []}
      searchText="Søker"
      placeholder="Søk i brønnøysundregistrene..."
      promptText="Skriv for å søke"
      emptyLabel="Fant ingen treff"
      maxResults={10}
      onChange={(e: BrregEnhetResponse[]) => handleEnhetSelected(e[0])}
      onSearch={(q) => lookupQueryInBrreg(q)}
      isInvalid={(!isLoading && props.isInvalid) || undefined}
      isValid={(!isLoading && props.isValid) || undefined}
      ref={typeaheadRef}
      selected={selected ? [selected] : []}
    />
  );
};

export default BrregTypeAhead;
